<!--
  StatusBadge.vue — Severity / status indicator chip.
  
  Displays a colored badge for severity levels or agent status.
  Can render as a dot, chip, or inline text.
  
  Props:
    severity — 'critical' | 'high' | 'medium' | 'low' | 'info' | 'ok'
    variant  — 'chip' | 'dot' | 'text' (default: 'chip')
    size     — 'small' | 'default' | 'large'
    label    — Override badge text (defaults to severity name)
    pulse    — boolean: show animated pulse (for live/active states)
-->
<template>
  <!-- Chip variant -->
  <VChip
    v-if="variant === 'chip'"
    :color="color"
    :size="size"
    variant="tonal"
    class="status-badge-chip font-weight-semibold text-uppercase"
  >
    <span v-if="pulse" class="pulse-indicator mr-1" :style="{ background: color }" />
    {{ displayLabel }}
  </VChip>

  <!-- Dot variant (compact, no text) -->
  <span
    v-else-if="variant === 'dot'"
    class="status-badge-dot"
    :class="{ 'pulse-dot': pulse }"
    :style="{ background: color }"
    :title="displayLabel"
  />

  <!-- Text variant (inline colored text) -->
  <span
    v-else
    class="status-badge-text font-weight-semibold text-uppercase"
    :style="{ color }"
  >
    {{ displayLabel }}
  </span>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  severity: {
    type: String,
    required: true,
    validator: v => ['critical', 'high', 'medium', 'low', 'info', 'ok'].includes(v),
  },
  variant: {
    type: String,
    default: 'chip',
    validator: v => ['chip', 'dot', 'text'].includes(v),
  },
  size: {
    type: String,
    default: 'small',
  },
  label: { type: String, default: '' },
  pulse: { type: Boolean, default: false },
})

const SEVERITY_COLORS = {
  critical: '#EF4444',
  high:     '#F97316',
  medium:   '#F59E0B',
  low:      '#3B82F6',
  info:     '#8B5CF6',
  ok:       '#22C55E',
}

const SEVERITY_LABELS = {
  critical: 'dashboard.severity.critical',
  high:     'dashboard.severity.high',
  medium:   'dashboard.severity.medium',
  low:      'dashboard.severity.low',
  info:     'dashboard.severity.info',
  ok:       'dashboard.severity.ok',
}

const color        = computed(() => SEVERITY_COLORS[props.severity] || '#9E9E9E')
const displayLabel = computed(() => props.label || (SEVERITY_LABELS[props.severity] ? t(SEVERITY_LABELS[props.severity]) : props.severity))
</script>

<style scoped>
.status-badge-chip {
  letter-spacing: 0.5px;
  font-size: 0.7rem;
}

.status-badge-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  vertical-align: middle;
}

.status-badge-text {
  font-size: 0.75rem;
  letter-spacing: 0.5px;
}

.pulse-indicator {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  animation: pulse-ring 1.5s infinite;
}

@keyframes pulse-ring {
  0%   { box-shadow: 0 0 0 0 rgba(var(--v-theme-on-surface), 0.4); }
  70%  { box-shadow: 0 0 0 4px rgba(var(--v-theme-on-surface), 0); }
  100% { box-shadow: 0 0 0 0 rgba(var(--v-theme-on-surface), 0); }
}
</style>
